<template>
  <div class="page-container">
    <div class="auth-container">
      <div class="glass-wrapper">
        <div class="border-effect"></div>
        <div class="glass-container mx-2">
          <img :src="logo" alt="Mastermind Logo" class="logo" />
          <LoginForm
            :errorMessage="errorMessage"
            @loginEvent="handleLogin"
            :userEmail="email"
            @updateEmail="handleEmailUpdate"
            @updateErrorMessage="handleErrorMessageUpdate"
          />
        </div>
      </div>
    </div>
    <button
      v-if="emailInputValue"
      type="submit"
      form="loginForm"
      class="absolute bottom-4 right-0 lg:right-4 pb-8 border-0 z-10"
    >
      <img
        src="@/assets/continue-button.svg"
        alt="Continue"
        class="cursor-pointer"
        width="250"
      />
    </button>
    <ContactFooter />
  </div>
</template>

<script setup>
import LoginForm from "@/components/auth/LoginForm.vue";
import ContactFooter from "@/components/general/ContactFooter.vue";
import { createClient } from "@/services/stripe/stripe.js";
import { getNumberDependents } from "@/services/user/user.js";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

const store = useStore();
const router = useRouter();
const route = useRoute();
const logo = ref(require("@/assets/brain-white-logo.png"));

const setUserIsLogged = (value) => store.commit("user/setUserIsLogged", value);

const login = async (email, password) =>
  store.dispatch("user/login", { email, password });

const user = computed(() => store.getters["user/getUserData"]);

const errorMessage = ref(null);
const email = ref(null);
const emailInputValue = ref("");

function handleErrorMessageUpdate(value) {
  errorMessage.value = value;
}

onMounted(() => {
  email.value = route.query.email;
});

function handleEmailUpdate(value) {
  emailInputValue.value = value;
}

async function handleLogin(email, password, navigateTo) {
  const loginResponse = await login(email, password);

  await validateLoginResponse(loginResponse, navigateTo);
}

async function validateLoginResponse(loginResponse, navigateTo) {
  if (!loginResponse.isLogged) {
    errorMessage.value = "Sorry, your email or password is incorrect.";
    return;
  }

  if (!user.value["is_active"]) {
    sessionStorage.setItem("parentEmail", user.value["email"]);
    await getOrCreateClient(user.value["email"], user.value["phoneNumber"]);
    return;
  }

  if (
    !user.value["mfaActivated"] &&
    user.value["role"] === "Mastermind_admin"
  ) {
    router.push({ name: "TOTP Register" });
    return;
  }

  if (user.value["role"] === "Mastermind_admin") {
    router.push({ name: "TOTP Login" });
    return;
  }

  const dependents = await verifyNumberDependents();
  if (dependents > 0) {
    sessionStorage.setItem("parentEmail", loginResponse.email);
    return router.push({
      name: "Register players",
      query: {
        n: dependents,
        registrationMethod: loginResponse.isFreeTrial ? "free-trial" : "B2C",
      },
    });
  }

  setUserIsLogged(true);

  if (navigateTo === "/settings/players") {
    return router.replace({
      name: "Players",
    });
  } else {
    return router.replace({
      name: "Home",
      query: { messages: "true" },
    });
  }
}

async function verifyNumberDependents() {
  const responseNumberDependents = await getNumberDependents(user.value["id"]);
  return responseNumberDependents.data.dependents_to_register;
}

async function getOrCreateClient(email, phoneNumber) {
  const response = await createClient(email, user.value["userName"]);

  if (response) {
    const location = user.value["location"];
    if (response.success) {
      router.push({
        name: "Check Out",
        query: {
          customerId: response.data.customer.id,
          s: !location,
          l: location,
          email,
          phoneNumber,
        },
      });
      return;
    }
  }
}
</script>

<style scoped>
.page-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-image: url("../../assets/background-login-white-label.png");
  background-size: cover;
  background-position: center;
}

.auth-container {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.glass-wrapper {
  position: relative;
  width: 100%;
  max-width: 460px;
}

.border-effect {
  position: absolute;
  top: 28px;
  left: 20px;
  width: 96%;
  height: 96%;
  border-right: 2px solid rgba(255, 255, 255, 0.5);
  border-bottom: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 30px;
  z-index: 0;
}

.glass-container {
  position: relative;
  z-index: 1;
  background: rgba(64, 64, 64, 0.25);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 30px;
  border: 9px solid rgba(255, 255, 255, 0.3);
  padding: 0rem 1rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo {
  width: 50%;
  max-width: 250px;
}
</style>
